import * as React from 'react';
import closeIcon from '@/assets/images/svg/close_icon.svg';
import type { Echo } from '@/types';
import { GroupChatEchoChip } from './GroupChatEchoChip';

/** Props for the {@link GroupChatHeader} component. */
interface GroupChatHeaderProps {
  /** Full echo catalogue keyed by ID. */
  echoes: Record<string, Echo>;
  /** Currently selected echoes for the group. */
  selectedEchoes: Echo[];
  /** Callback when an echo is added via search dropdown. */
  onSelectEcho: (echo: Echo) => void;
  /** Callback when a selected echo chip is removed. */
  onRemoveEcho: (echoId: string) => void;
  /** Callback to exit group creation mode. */
  onCancel: () => void;
}

const MAX_SELECTIONS = 2;

/**
 * Header shown during group chat creation.
 * Provides a search input with autocomplete dropdown and selected echo chips.
 */
export const GroupChatHeader: React.FC<GroupChatHeaderProps> = ({
  echoes,
  selectedEchoes,
  onSelectEcho,
  onRemoveEcho,
  onCancel,
}) => {
  const [query, setQuery] = React.useState('');
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedIds = React.useMemo(
    () => new Set(selectedEchoes.map((e) => e.id)),
    [selectedEchoes]
  );

  const filteredEchoes = React.useMemo(() => {
    if (!query.trim()) return [];
    const lower = query.toLowerCase();
    return Object.values(echoes).filter(
      (echo) => echo.name.toLowerCase().includes(lower) && !selectedIds.has(echo.id)
    );
  }, [query, echoes, selectedIds]);

  React.useEffect(() => {
    setIsDropdownOpen(filteredEchoes.length > 0);
    setHighlightedIndex(0);
  }, [filteredEchoes]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectEcho = (echo: Echo) => {
    onSelectEcho(echo);
    setQuery('');
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isDropdownOpen && filteredEchoes[highlightedIndex]) {
        selectEcho(filteredEchoes[highlightedIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredEchoes.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  const atLimit = selectedEchoes.length >= MAX_SELECTIONS;

  return (
    <>
      <div className='mb-6 flex items-center gap-3'>
        <button
          onClick={onCancel}
          className='p-1 transition-opacity hover:opacity-70'
          aria-label='Cancel group creation'
        >
          <img src={closeIcon} alt='Cancel' className='size-6' />
        </button>
        <span className='font-sans text-[15px] font-semibold leading-[21px] tracking-normal text-gray-600'>
          Add group members
        </span>
      </div>

      <div className='relative mb-6' ref={containerRef}>
        {selectedEchoes.length > 0 && (
          <div className='mb-2 flex flex-wrap gap-2'>
            {selectedEchoes.map((echo) => (
              <GroupChatEchoChip key={echo.id} echo={echo} onRemove={() => onRemoveEcho(echo.id)} />
            ))}
          </div>
        )}

        <input
          ref={inputRef}
          type='text'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (filteredEchoes.length > 0) setIsDropdownOpen(true);
          }}
          placeholder={atLimit ? 'Maximum 2 echoes selected' : 'Add up to 2 Echoes'}
          disabled={atLimit}
          className='w-full border-0 border-b border-black-50 bg-transparent pb-2 font-sans text-[12px] font-medium leading-[21px] tracking-normal text-brand-800 placeholder:text-gray-500 focus:border-black-50 focus:outline-none focus:ring-0 disabled:opacity-50'
        />

        {isDropdownOpen && filteredEchoes.length > 0 && (
          <ul className='absolute inset-x-0 z-20 mt-1 max-h-[200px] overflow-y-auto rounded-lg bg-white shadow-lg'>
            {filteredEchoes.map((echo, index) => (
              <li key={echo.id}>
                <button
                  type='button'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectEcho(echo)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                    index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {echo.imageUrl ? (
                    <img
                      src={echo.imageUrl}
                      alt={echo.name}
                      className='size-8 rounded-full object-cover'
                    />
                  ) : (
                    <div className='flex size-8 items-center justify-center rounded-full bg-gray-340 font-proxima text-[10px] font-bold uppercase text-brand-800'>
                      {echo.name.slice(0, 2)}
                    </div>
                  )}
                  <span className='font-sans text-[13px] font-medium text-brand-800'>
                    {echo.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};
